import {Component, Input, numberAttribute, ViewChild, inject} from '@angular/core';
import {Report} from "../model/report";
import {DatePipe, NgClass, NgForOf, NgIf} from "@angular/common";
import {Router, RouterLink, ActivatedRoute} from "@angular/router";
import {ChartComponent, NgApexchartsModule} from "ng-apexcharts";
import {
  ApexNonAxisChartSeries,
  ApexResponsive,
  ApexChart,
  ApexPlotOptions,
} from "ng-apexcharts";
import {DeviceDetectorService} from "ngx-device-detector";
import { RestService, ResultFeedback } from '../service/rest/rest.service';
import { Question } from '../model/question';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { AuthService } from '../service/auth/auth.service';
import { LoggerService } from '../service/utils/logger.service';
import { FormsModule } from '@angular/forms';
import { SkyCoachPanelComponent } from '../sky-coach/sky-coach-panel.component';
import { SkyTransitionComponent } from '../sky-coach/sky-transition.component';
import { SkyCoachContext } from '../service/sky/sky-coach-state.service';
import { PlatformService } from '../service/platform/platform.service';
import { BrandService } from '../service/brand/brand.service';

export type ChartOptions = {
  series: ApexNonAxisChartSeries;
  chart: ApexChart;
  responsive: ApexResponsive[];
  labels: any;
  legend: { show: boolean };
  dataLabels: { enabled: boolean };
  plotOptions: ApexPlotOptions;
};

@Component({
  selector: 'app-question-pool-results',
  standalone: true,
  imports: [
    DatePipe,
    NgApexchartsModule,
    NgForOf,
    NgIf,
    NgClass,
    FormsModule,
    RouterLink,
    SkyCoachPanelComponent,
    SkyTransitionComponent,
  ],
  templateUrl: './question-pool-results.component.html',
  styleUrl: './question-pool-results.component.css'
})
export class QuestionPoolResultsComponent {
  protected readonly brand = inject(BrandService);

  protected mobile: boolean = false;
  private _reportId: number | undefined;
  protected report: Report | undefined;
  /** Prevents concurrent getReportData() for the same reportId (avoids report flashing empty after clearCurrentTestState). */
  private loadingReportId: number | null = null;
  /** ReportId we have already successfully loaded; skip reload when paramMap emits again (e.g. after study->results). */
  private loadedReportId: number | null = null;
  protected showCorrectAnswers: boolean = true;
  protected showWrongAnswers: boolean = true;
  protected recreateLoading = false;
  protected resultFeedback: ResultFeedback | null = null;
  protected resultFeedbackLoading = false;
  /** True after the first result-feedback attempt finishes (success or fail). */
  protected resultFeedbackReady = false;
  protected showResultTransition = false;
  private resultTransitionRequested = false;
  private resultTransitionShown = false;
  private skyCoachContextCache?: SkyCoachContext;
  protected chartOptions: ChartOptions = {
    series: [],
    chart: { type: 'donut', width: 156, height: 156 },
    labels: ['Correct', 'Wrong', 'Not answered'],
    legend: { show: false },
    dataLabels: { enabled: false },
    plotOptions: {
      pie: {
        donut: {
          size: '74%',
          labels: {
            show: true,
            name: { show: false },
            value: {
              show: true,
              fontSize: '1.25rem',
              fontWeight: 750,
              color: '#172033',
              offsetY: 4,
              formatter: () => `${this.scorePercent()}%`,
            },
            total: {
              show: true,
              showAlways: true,
              label: '',
              fontSize: '1.25rem',
              fontWeight: 750,
              color: '#172033',
              formatter: () => `${this.scorePercent()}%`,
            },
          },
        },
      },
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: {
            width: 136,
            height: 136,
          },
          legend: {
            show: false,
          },
          plotOptions: {
            pie: {
              donut: {
                size: '74%',
                labels: {
                  value: { fontSize: '1.125rem' },
                  total: { fontSize: '1.125rem' },
                },
              },
            },
          },
        },
      },
    ],
  };

  ngOnInit() {
    this.mobile = this.deviceService.isMobile();
    this.resultTransitionRequested =
      this.platform.isMobile()
      && typeof history !== 'undefined'
      && history.state?.skyTransition === true;
    // Read reportId from route parameter
    const routeReportId = this.route.snapshot.paramMap.get('reportId');
    console.log('[ngOnInit] Route reportId:', routeReportId, 'current _reportId:', this._reportId);
    console.log('[ngOnInit] currentUserTestId in service:', this.restService.currentUserTestId);
    
    if (routeReportId) {
      const userTestId = +routeReportId;
      LoggerService.log(`[ngOnInit] Read reportId (userTestId) from route: ${userTestId}`);
      console.log(`[ngOnInit] Setting _reportId to: ${userTestId}`);
      this._reportId = userTestId;
      // ALWAYS set currentUserTestId from route parameter - never trust existing value
      console.log(`[ngOnInit] CRITICAL: Setting currentUserTestId to route value: ${userTestId} (was: ${this.restService.currentUserTestId})`);
      LoggerService.log(`[ngOnInit] Setting currentUserTestId from route: ${userTestId} (was: ${this.restService.currentUserTestId})`);
      this.restService.currentUserTestId = userTestId;
      this.getReportData();
    } else {
      LoggerService.error('[ngOnInit] No reportId in route, cannot generate report', null);
      console.error('[ngOnInit] No reportId in route');
      this.router.navigate(['question-pool']);
    }
    
    // Subscribe to route parameter changes in case user navigates to a different report
    this.route.paramMap.subscribe(params => {
      const newReportId = params.get('reportId');
      console.log('[route paramMap subscription] New reportId:', newReportId, 'current _reportId:', this._reportId);
      if (newReportId) {
        const userTestId = +newReportId;
        if (this._reportId !== userTestId) {
          LoggerService.log(`[route paramMap] Route changed, new reportId (userTestId): ${userTestId} (was: ${this._reportId})`);
          console.log(`[route paramMap] CRITICAL: Updating _reportId and currentUserTestId to: ${userTestId}`);
          this._reportId = userTestId;
          this.loadedReportId = null; // allow load for the new report
          this.restService.currentUserTestId = userTestId;
          this.getReportData();
        }
      }
    });
  }

  // report id getter and setter (kept for compatibility, but route param is primary)
  @Input({required:false, transform: numberAttribute})
  set reportId(value: number|undefined) {
    this._reportId = value;
    // Use reportId (which is now userTestId) to set currentUserTestId in RestService
    if (value && this.restService.currentUserTestId !== value) {
      LoggerService.log(`[reportId setter] Setting currentUserTestId from input: ${value} (was: ${this.restService.currentUserTestId})`);
      this.restService.currentUserTestId = value;
    }
    if (value) {
      this.getReportData();
    }
  }
  get reportId(): number|undefined {
    return this._reportId;
  }
  constructor(
      private deviceService: DeviceDetectorService,
      protected restService: RestService,
      private sanitizer: DomSanitizer,
      protected router: Router,
      private route: ActivatedRoute,
      private authService: AuthService,
      private platform: PlatformService) {
        // RestService is injected via constructor
        console.log('[QuestionPoolResultsComponent] Constructor called');
        console.log('[QuestionPoolResultsComponent] currentUserTestId in service:', this.restService.currentUserTestId);
  }

  sanitizeHtml(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }
  
  // gets report data
  private async getReportData() {
    console.log('[getReportData] START - reportId:', this._reportId, 'currentUserTestId:', this.restService.currentUserTestId);
    console.log('[getReportData] Route snapshot params:', this.route.snapshot.paramMap.get('reportId'));
    
    // CRITICAL: Always use reportId from route, never rely on currentUserTestId
    if (!this._reportId) {
      LoggerService.error('[getReportData] No reportId available, cannot generate report', null);
      console.error('[getReportData] No reportId, navigating away');
      this.router.navigate(['question-pool']);
      return;
    }
    // Prevent concurrent or duplicate load for same reportId. A second call (e.g. paramMap emitting
    // after study component is torn down) can run after clearCurrentTestState() and build report from
    // empty questionsMap, overwriting the good report with an empty one.
    if (this.loadingReportId === this._reportId) {
      console.log('[getReportData] Skipping concurrent load for same reportId:', this._reportId);
      return;
    }
    if (this.loadedReportId === this._reportId) {
      console.log('[getReportData] Already loaded this reportId:', this._reportId);
      return;
    }
    this.loadingReportId = this._reportId;
    try {
      // ALWAYS set currentUserTestId from route parameter - never trust the service value
      const routeUserTestId = this._reportId;
    console.log(`[getReportData] CRITICAL: Setting currentUserTestId to route value: ${routeUserTestId} (was: ${this.restService.currentUserTestId})`);
    LoggerService.log(`[getReportData] Using route reportId (userTestId): ${routeUserTestId}`);
    this.restService.currentUserTestId = routeUserTestId;
    
    LoggerService.log(`[getReportData] Generating report for userTestId: ${this.restService.currentUserTestId}`);
    console.log('[getReportData] Calling restService.getReport() with currentUserTestId:', this.restService.currentUserTestId);
    this.report = await this.restService.getReport();
    console.log('[getReportData] Report received:', this.report ? `${this.report.questions?.length || 0} questions` : 'null');
    if (this.report && this.report.questions && this.report.questions.length > 0) {
      this.loadedReportId = this._reportId ?? null;
      console.log('[getReportData] Report question IDs:', this.report.questions.map(q => q.id));
      console.log('[getReportData] Report question titles (first 3):', this.report.questions.slice(0, 3).map(q => q.title?.substring(0, 50)));
    }
    this.prepareChart();

    // Wait for retention feedback before mounting Sky so the first pose is final.
    this.resultFeedbackReady = false;
    this.resultFeedbackLoading = true;
    try {
      await this.updateUserTestWithResults();
      // Always load Sky/retention feedback, even when the update step fails
      // (already finished tests, retries, refresh on this page).
      if (this._reportId != null && !this.resultFeedback) {
        await this.loadResultFeedback(this._reportId);
      }
    } finally {
      this.resultFeedbackLoading = false;
      this.resultFeedbackReady = true;
      this.skyCoachContextCache = undefined;
    }
    this.showRequestedResultTransition();

    if (!this.report || this.report.questions.length == 0) {
      // Do NOT auto-navigate away: it causes "flash then jump" behavior and hides debugging info.
      // Keep the user on the report page so they can see what happened.
      console.warn('[getReportData] Invalid report (no questions). Staying on results page.');
    }
    } finally {
      this.loadingReportId = null;
    }
  }

  private async updateUserTestWithResults() {
    if (!this.restService.currentUserTestId || !this.report) {
      return;
    }

    try {
      const user = this.authService.getUser();
      const sessionId = user?.sid;
      if (!sessionId) {
        LoggerService.error('User not authenticated', null);
        return;
      }

      const totalQuestions = this.countQuestions();
      const correctAnswers = this.countCorrect();
      const percentage = totalQuestions > 0 ? (correctAnswers / totalQuestions) * 100 : 0;
      const passed = this.testPassed() ? 1 : 0;
      const timeEnd = new Date().toISOString();

      const success = await this.restService.updateUserTest(
        sessionId,
        this.restService.currentUserTestId,
        percentage,
        passed === 1,
        timeEnd
      );

      if (success) {
        LoggerService.log(`User test ${this.restService.currentUserTestId} updated with results`);
        await this.loadResultFeedback(this.restService.currentUserTestId);
        // Clear current test state once results are persisted.
        // (Exam timer is stopped on finish; reportId is taken from the route, so we can safely clear here.)
        this.restService.clearCurrentTestState();
      } else {
        LoggerService.error('Failed to update user test results', null);
      }
    } catch (error) {
      LoggerService.error('Error updating user test:', error);
    }
  }

  protected async openRecommendedSession(): Promise<void> {
    const user = this.authService.getUser();
    const sessionId = user?.sid;
    const recommendation = this.resultFeedback?.nextRecommendation;
    if (sessionId && recommendation) {
      await this.restService.acceptTrainNextRecommendation(sessionId, recommendation);
    }
    if (recommendation?.recommendationType === 'SET_COURSE_GOAL') {
      await this.router.navigate(['/dashboard']);
      return;
    }
    if (recommendation) {
      this.restService.clearRecreateSettings();
      this.restService.setRecreateSettings(this.restService.buildTrainNextPrefill(recommendation));
    }
    await this.router.navigate(['/question-pool']);
  }

  protected closeResultTransition(): void {
    this.showResultTransition = false;
  }

  protected recommendedActionLabel(): string {
    if (this.resultFeedback?.nextRecommendation?.recommendationType === 'SET_COURSE_GOAL') {
      return 'Set target';
    }
    return 'Open New Test';
  }

  protected skyCoachContext(): SkyCoachContext {
    const feedback = this.resultFeedback;
    const wrongCount = this.countWrong();
    const total = this.countQuestions();
    const next: SkyCoachContext = {
      surface: 'results',
      recommendationType: feedback?.nextRecommendation?.recommendationType,
      readinessStatus: feedback?.readiness?.status,
      remainingToday: feedback?.dailyTarget?.remainingToday,
      onTrack: feedback?.dailyTarget?.onTrack,
      streakDay: feedback?.streakDay,
      reason: feedback?.nextRecommendation?.reason,
      subjectName: feedback?.readiness?.subjectName ?? feedback?.nextRecommendation?.subjectName,
      testPassed: this.report ? this.testPassed() : null,
      isExamMode: this.isExamMode(),
      weakTopicsCount: feedback?.weakTopics?.length ?? 0,
      dailyGoalMet: feedback?.dailyGoalMet,
      allCorrect: total > 0 && wrongCount === 0 && this.countUnanswered() === 0,
    };
    const prev = this.skyCoachContextCache;
    if (
      prev
      && prev.recommendationType === next.recommendationType
      && prev.readinessStatus === next.readinessStatus
      && prev.remainingToday === next.remainingToday
      && prev.onTrack === next.onTrack
      && prev.streakDay === next.streakDay
      && prev.reason === next.reason
      && prev.subjectName === next.subjectName
      && prev.testPassed === next.testPassed
      && prev.isExamMode === next.isExamMode
      && prev.weakTopicsCount === next.weakTopicsCount
      && prev.dailyGoalMet === next.dailyGoalMet
      && prev.allCorrect === next.allCorrect
    ) {
      return prev;
    }
    this.skyCoachContextCache = next;
    return next;
  }

  protected scorePercent(): number {
    const total = this.countQuestions();
    if (total <= 0) {
      return 0;
    }
    return Math.round((this.countCorrect() / total) * 1000) / 10;
  }

  protected correctCount(): number {
    return this.countCorrect();
  }

  protected wrongCount(): number {
    return this.countWrong();
  }

  protected unansweredCount(): number {
    return this.countUnanswered();
  }

  private showRequestedResultTransition(): void {
    if (!this.resultTransitionRequested || this.resultTransitionShown || !this.report) {
      return;
    }
    this.resultTransitionShown = true;
    this.showResultTransition = true;
  }

  private async loadResultFeedback(userTestId: number): Promise<void> {
    const user = this.authService.getUser();
    const sessionId = user?.sid;
    if (!sessionId) {
      this.resultFeedback = null;
      return;
    }
    this.resultFeedbackLoading = true;
    try {
      this.resultFeedback = await this.restService.getResultFeedback(sessionId, userTestId);
    } finally {
      this.resultFeedbackLoading = false;
    }
  }

  protected formatReadinessStatus(status?: string | null): string {
    if (!status) {
      return 'Not started';
    }
    return status
      .split('_')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }
  // prepares chart
  private prepareChart(){
    let correct = this.countCorrect();
    let wrong = this.countWrong();
    let unanswered = this.countUnanswered();
    this.chartOptions = {
      ...this.chartOptions,
      series: [correct, wrong, unanswered],
    };
  }
  // counts correct answers count
  private countCorrect():number{
    let count = 0;
    this.report?.questions.forEach(function (question){
      if(question.answered && question.correctAnswer){
        count++;
      }
    });
    return count;
  }
  // counts wrong answers count
  protected countWrong():number{
    let count = 0;
    this.report?.questions.forEach(function (question){
      if(question.answered && !question.correctAnswer){
        count++;
      }
    });
    return count;
  }
  // counts not answered count
  protected countUnanswered():number{
    let count = 0;
    this.report?.questions.forEach(function (question){
      if(!question.answered){
        count++;
      }
    });
    return count;
  }
  // counts not answered count
  protected countQuestions():number{
    
    if (this.report && this.report?.questions)
      return this.report?.questions?.length;
    return 0;
  }
  // calculates if test passed
  protected testPassed():boolean{
    let correct = 0;
    let count = 0;
    this.report?.questions.forEach(function (question){
      if(question.answered && question.correctAnswer){
        correct++;
      }
      count++;
    });

    if (correct/count >= 0.75)
      return true;
    return false;
  }

  // checks if the test was in exam mode
  protected isExamMode(): boolean {
    // Check if mode is 'exam' from the report or RestService
    const report = this.report as any;
    if (report?.mode === 'exam') {
      return true;
    }
    // Fallback to RestService if report doesn't have mode
    return this.restService.currentUserTestMode === 'exam';
  }

  // Get filtered questions based on show/hide toggles
  protected getFilteredQuestions(): Question[] {
    if (!this.report || !this.report.questions) {
      return [];
    }
    return this.report.questions.filter(question => {
      if (!question.answered) {
        return false; // Don't show unanswered questions
      }
      if (question.correctAnswer && !this.showCorrectAnswers) {
        return false; // Hide correct if toggle is off
      }
      if (!question.correctAnswer && !this.showWrongAnswers) {
        return false; // Hide wrong if toggle is off
      }
      return true;
    });
  }

  // Check if question is correct
  protected isCorrect(question: Question): boolean {
    return question.answered && question.correctAnswer === true;
  }

  /** Open New Test with the same parameters as this report (Saved Tests → Recreate). */
  async startAnotherTest(): Promise<void> {
    const testId = this._reportId;
    if (!testId) {
      return;
    }
    try {
      const user = this.authService.getUser();
      const sessionId = user?.sid;
      if (!sessionId) {
        alert('Please log in to start another test.');
        return;
      }
      this.recreateLoading = true;
      this.restService.clearRecreateSettings();
      this.restService.clearCurrentTestState();
      const resumeData = await this.restService.getTestForResume(sessionId, testId);
      if (!resumeData) {
        alert('Failed to load test settings. The test may have been deleted.');
        return;
      }
      const settings = this.restService.buildRecreateSettings(resumeData);
      this.restService.setRecreateSettings(settings);
      await this.router.navigate(['/question-pool'], { state: { recreateSettings: settings } });
    } catch (error) {
      LoggerService.error('Failed to start another test with saved settings:', error);
      alert('Failed to open New Test with saved settings.');
    } finally {
      this.recreateLoading = false;
    }
  }
}
